import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { getCurrencyFromCountry } from '../../redux/slices/currencySlice';

// This component displays a confirmation dialog for country selection
// It warns the user that their country selection will affect their currency and cannot be changed later
const CountryConfirmDialog = ({ 
  open, 
  setOpen, 
  detectedCountry, 
  countryName, 
  onConfirm,
  onCancel
}) => {
  const cancelButtonRef = useRef(null);
  
  // Get the currency for this country
  const currency = getCurrencyFromCountry(detectedCountry);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Confirm Your Country
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        We've detected your country as <span className="font-bold">{countryName}</span> ({detectedCountry}).
                        This will set your account currency to <span className="font-bold">{currency}</span>.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        <span className="text-red-500 font-semibold">Important:</span> This country selection cannot be changed later 
                        and will affect all transactions in your account.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="inline-flex w-full justify-center rounded-md sm:col-start-2"
                    onClick={() => {
                      onConfirm(detectedCountry);
                      setOpen(false);
                    }}
                  >
                    Confirm Country
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-3 inline-flex w-full justify-center rounded-md sm:col-start-1 sm:mt-0"
                    onClick={() => {
                      onCancel();
                      setOpen(false);
                    }}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CountryConfirmDialog; 